import { MouseEvent, useCallback } from 'react';

import { useCountries } from 'api/requests/country';
import {
  useDeleteLegalClient,
  useLegalClient as useLegalClientApi,
  useLegalClientEnableBrightSync,
  useLegalClientManagers,
} from 'api/requests/legal';
import { OnboardingStatusType, PermissionType } from 'api/types/entity';
import { NavigationBreadcrumbsTitles } from 'components';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form/Form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate, usePageTitle, RoutesId } from 'libs/navigation';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { isDownloadQuestionnaireStatusSufficient } from 'modules/client/common/helpers';
import {
  useCheckIfPersonalAccountIsEditing,
  useManagersForClients,
} from 'modules/client/common/hooks';

import { LegalClientFormType } from '../forms/legalClientForm';
import { getLegalClientInitialValue } from '../helpers';

import { useArchiveParticipants } from './useArchiveParticipants';
import { useBodyAction } from './useBodyAction';
import { useDeleteParticipants } from './useDeleteParticipants';
import { useDownloadLegalClientQuestionnaire } from './useDownloadLegalClientQuestionnaire';
import { useFormError } from './useFormError';
import { useLockLegalClient } from './useLockLegalClient';
import { useUpdateLegalClient } from './useUpdateLegalClient';

type LegalClientContainerParams = {
  legalClientId: string;
};

export const useLegalClient = () => {
  const { navigate, params } = useNavigate<LegalClientContainerParams>();

  const notify = useNotify();

  const { t } = useTranslation();

  const { checkPermissions } = usePermissions();

  const {
    updateLegalClient,
    loading: submitLoading,
    error: submitError,
  } = useUpdateLegalClient();

  const {
    error,
    legalClient,
    loading,
    refetch: refetchLegalClient,
  } = useLegalClientApi(params.legalClientId!);

  const { lockClientIfPossible, unlockClient } =
    useLockLegalClient(legalClient);

  const formError = useFormError(submitError);

  const { countries, loading: countriesLoading } = useCountries();

  const { managers, loading: managersLoading } = useManagersForClients(
    useLegalClientManagers
  );

  const {
    onDeleteBeneficiary,
    onDeleteHead,
    onDeleteLegalHead,
    onDeleteBranch,
  } = useDeleteParticipants(legalClient, refetchLegalClient);

  const bodyActions = useBodyAction(legalClient, refetchLegalClient);

  const {
    onArchiveHead,
    onArchiveLegalHead,
    onDearchiveHead,
    onDearchiveLegalHead,
    onArchiveBeneficiary,
    onDearchiveBeneficiary,
    onArchiveBranch,
    onDearchiveBranch,
  } = useArchiveParticipants(legalClient, refetchLegalClient);

  const { isEditablePersonalAccountNumber } =
    useCheckIfPersonalAccountIsEditing({
      isAccountOpened:
        legalClient?.onboardingStatus === OnboardingStatusType.AccountOpened,
      isCreatedBySystem:
        legalClient?.personalAccount.isCreatedBySystem ?? false,
    });

  const { enableBrightSync, error: errorEnableBrightSync } =
    useLegalClientEnableBrightSync();

  const { downloadLegalClientQuestionnaire } =
    useDownloadLegalClientQuestionnaire();

  const {
    deleteLegalClient,
    loading: deleteLegalClientLoading,
    error: deleteLegalClientError,
  } = useDeleteLegalClient();

  const initialValues = legalClient
    ? getLegalClientInitialValue(t, legalClient)
    : undefined;

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.legalClient]: initialValues?.companyName.fullName,
  };

  const onSubmit: FormSubmit<LegalClientFormType> = async (values, helpers) => {
    if (
      await updateLegalClient(
        legalClient!,
        values,
        isEditablePersonalAccountNumber
      )
    ) {
      helpers.setReadOnly(true);
    }
  };

  const onClickHead = useCallback(
    (id: string) => {
      if (legalClient)
        navigate(
          ROUTES.clients.legalClients.legalClient.legalClientHead.fullPath,
          {
            legalClientId: legalClient.id,
            headId: id,
          }
        );
      return undefined;
    },
    [legalClient, navigate]
  );

  const onClickLegalHead = useCallback(
    (id: string) => {
      if (legalClient)
        navigate(
          ROUTES.clients.legalClients.legalClient.legalClientLegalHead.fullPath,
          {
            legalClientId: legalClient.id,
            headId: id,
          }
        );

      return undefined;
    },
    [legalClient, navigate]
  );

  const onClickBeneficiary = (beneficiaryId: string) => {
    if (legalClient)
      navigate(
        ROUTES.clients.legalClients.legalClient.legalClientBeneficiary.fullPath,
        {
          legalClientId: legalClient.id,
          beneficiaryId,
        }
      );

    return undefined;
  };

  const onClickBranch = useCallback(
    (branchId: string) => {
      if (legalClient)
        navigate(
          ROUTES.clients.legalClients.legalClient.legalClientBranch.fullPath,
          {
            legalClientId: legalClient.id,
            branchId,
          }
        );
      return undefined;
    },
    [legalClient, navigate]
  );

  const onShowVersionHistory =
    legalClient?.versionNumber && legalClient?.versionNumber > 1
      ? () => {
          navigate(
            ROUTES.clients.legalClients.legalClient.legalClientVersion.fullPath,
            {
              legalClientId: legalClient.id,
              versionId: legalClient.versionId!,
            }
          );
        }
      : undefined;

  const onLinkToBright =
    !legalClient?.isBrightSyncEnabled &&
    checkPermissions([PermissionType.Client_LegalClient_EnableBrightSync])
      ? async () => {
          if (await lockClientIfPossible()) {
            const res = await enableBrightSync(
              legalClient!.id!,
              legalClient!.versionNumber!
            );
            if (res) {
              notify.info(t('client.client.statuses.updateBrightText'));
            }
          }
        }
      : undefined;

  const onDownloadLegalClientQuestionnaire =
    legalClient &&
    isDownloadQuestionnaireStatusSufficient(legalClient.onboardingStatus) &&
    checkPermissions([PermissionType.Client_LegalClient_ExportQuestionnaire])
      ? () => downloadLegalClientQuestionnaire(legalClient.id)
      : undefined;

  const onDeleteLegalClient =
    legalClient?.onboardingStatus === OnboardingStatusType.FillingOut &&
    checkPermissions([PermissionType.Client_LegalClient_DeleteIncompleteOne])
      ? async () => {
          if (await deleteLegalClient(legalClient.id)) {
            navigate(
              ROUTES.clients.legalClients.fullPath,
              {},
              { replace: true }
            );
          }
        }
      : undefined;

  const onClickRepresentative = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!legalClient?.representativeId) {
        return;
      }

      navigate(
        ROUTES.clients.legalClients.legalClient.legalRepresentative.fullPath,
        {
          legalClientRepresentativeId: legalClient.representativeId,
          legalClientId: legalClient.id,
        },
        { event }
      );
    },
    [navigate, legalClient]
  );

  usePageTitle(legalClient?.fullName ?? '');

  useServerErrorNotify(errorEnableBrightSync || deleteLegalClientError);

  return {
    legalClient,
    loading: !legalClient && loading,
    countries: countries ?? [],
    countriesLoading,
    error,
    initialValues,
    submitLoading,
    formError,
    breadcrumbsTitles,
    managers,
    managersLoading,
    params,
    isEditablePersonalAccountNumber,
    deleteLegalClientLoading,
    onLinkToBright,
    onDownloadLegalClientQuestionnaire,
    onDeleteLegalClient,
    onDeleteBeneficiary,
    onSubmit,
    onClickHead,
    onClickLegalHead,
    onClickBeneficiary,
    onClickBranch,
    lockClientIfPossible,
    unlockClient,
    onShowVersionHistory,
    refetchLegalClient,
    onDeleteHead,
    onDeleteLegalHead,
    onArchiveHead,
    onArchiveLegalHead,
    onDearchiveHead,
    onDearchiveLegalHead,
    onArchiveBeneficiary,
    onDearchiveBeneficiary,
    onArchiveBranch,
    onDearchiveBranch,
    onDeleteBranch,
    onClickRepresentative,
    ...bodyActions,
  };
};
